/**
 * DOM utilities including browser sniffing, position/size calculations,
 * SVG handling, throttle/debounce, and CSRF-protected fetch.
 */

// Type definitions
interface Position {
  left: number;
  top: number;
}

interface Size {
  width: number;
  height: number;
}

interface BottomRightPosition {
  right: number;
  bottom: number;
}

interface Sniffer {
  major: number;
  minor: number;
  ff: boolean;
  win: boolean;
  mac: boolean;
  /** @deprecated Always false - IE is no longer supported */
  ie: boolean;
  /** @deprecated Always false - IE is no longer supported */
  ie4: boolean;
  /** @deprecated Always false - Netscape 4 is no longer supported */
  ns: boolean;
  /** @deprecated Always false - Netscape 4 is no longer supported */
  ns4: boolean;
  /** @deprecated Always false - Netscape 4 is no longer supported */
  ns4b: boolean;
}

interface B6PNamespace {
  runAfterAsyncMergeReports: (fnc: () => void, fnc2?: () => void) => void;
  escapeHtml: (text: string) => string;
  showToast: (message: string, durationMs?: number) => void;
}

/////////////
// SNIFFER //
/////////////

/**
 * Creates a browser sniffer object for detecting platform and browser version.
 * Note: Browser sniffing is largely deprecated in favor of feature detection.
 * IE and Netscape 4 flags are kept for backwards compatibility but always return false.
 */
function createSniffer(): Sniffer {
  const agent = navigator.userAgent.toLowerCase();
  const major = parseInt(navigator.appVersion);
  const isMozilla = (agent.indexOf('mozilla') !== -1) && (agent.indexOf('spoofer') === -1) && (agent.indexOf('compatible') === -1);
  return {
    major,
    minor: parseFloat(navigator.appVersion),
    ff: isMozilla && (major >= 5),
    win: (agent.indexOf("win") !== -1),
    mac: (agent.indexOf("mac") !== -1),
    // Legacy flags - always false, kept for backwards compatibility
    ie: false,
    ie4: false,
    ns: false,
    ns4: false,
    ns4b: false
  };
}

const is: Sniffer = createSniffer();

/**
 * Finds the absolute position of an element on the page.
 * @param obj - The DOM element to find position of
 * @returns Position object with left and top coordinates
 */
function findPosition(obj: HTMLElement | null): Position {
  let curleft = 0;
  let curtop = 0;
  if (obj) {
    if (obj.offsetParent) {
      let current: HTMLElement | null = obj;
      while (current && current.offsetParent) {
        curleft += current.offsetLeft;
        curtop += current.offsetTop;
        current = current.offsetParent as HTMLElement;
      }
    } else if ((obj as any).x !== undefined) {
      curleft += (obj as any).x;
      curtop += (obj as any).y;
    }
  }
  return { left: curleft, top: curtop };
}

/**
 * Gets the height of an element.
 * @param obj - The DOM element
 * @returns The element's offsetHeight
 */
function getObjectHeight(obj: HTMLElement): number {
  return obj.offsetHeight;
}

/**
 * Gets the width of an element.
 * @param obj - The DOM element
 * @returns The element's offsetWidth
 */
function getObjectWidth(obj: HTMLElement): number {
  return obj.offsetWidth;
}

/**
 * Gets the size of an element.
 * @param obj - The DOM element
 * @returns Size object with width and height
 */
function getObjectSize(obj: HTMLElement): Size {
  return { width: obj.offsetWidth, height: obj.offsetHeight };
}

/**
 * Gets the current scroll position of the page.
 * @returns Position object with left and top scroll coordinates
 */
function getScrollPosition(): Position {
  return {
    left: window.pageXOffset || document.documentElement.scrollLeft || 0,
    top: window.pageYOffset || document.documentElement.scrollTop || 0
  };
}

/**
 * Gets the inner size of the browser window.
 * @returns Size object with width and height of the viewport
 */
function getInnerSize(): Size {
  return {
    width: window.innerWidth || document.documentElement.clientWidth || 0,
    height: window.innerHeight || document.documentElement.clientHeight || 0
  };
}

/**
 * Gets the bottom-right position of the visible viewport.
 * @returns Object with right and bottom coordinates
 */
function getScrollBottomRightPosition(): BottomRightPosition {
  const sz = getInnerSize();
  const pos = getScrollPosition();
  const bt = sz.height + pos.top - 1;
  const rt = sz.width + pos.left - 1;
  return { right: rt, bottom: bt };
}

/**
 * Detects the version of Internet Explorer.
 * @returns -1 always - IE is no longer supported
 * @deprecated IE is no longer supported, this function always returns -1
 */
function getInternetExplorerVersion(): number {
  return -1;
}

// SVG handling - Promise-based caching
let svgMap: Record<string, Promise<string>> = {};
let svgDeferreds: Promise<void>[] = [];

/**
 * Parses an SVG string and returns the SVG element.
 * @param svgString - The SVG string to parse
 * @returns The SVG element or null if not found
 */
function parseSvgString(svgString: string): SVGElement | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  return doc.querySelector('svg');
}

/**
 * Replaces an img element with inline SVG content.
 * @param img - The img element to replace
 * @param urlToDeferredMap - Map of URLs to Promises for caching
 * @param deferreds - Array of Promise objects for tracking completion
 */
function fixSVG(img: HTMLImageElement, urlToDeferredMap?: Record<string, Promise<string>>, deferreds?: Promise<void>[]): void {
  urlToDeferredMap = urlToDeferredMap || svgMap;
  deferreds = deferreds || svgDeferreds;

  const imgURL = img.getAttribute('src');
  if (!imgURL) return;

  let svgPromise = urlToDeferredMap[imgURL];

  if (!svgPromise) {
    svgPromise = fetch(imgURL).then(response => response.text());
    urlToDeferredMap[imgURL] = svgPromise;
  }

  const replacementPromise = svgPromise.then(svgData => {
    const svg = parseSvgString(svgData);
    if (!svg) return;

    // Clone the SVG for this instance
    const svgClone = svg.cloneNode(true) as SVGElement;

    // Copy attributes from img to svg
    const imgID = img.getAttribute('id');
    const imgClass = img.getAttribute('class');
    const imgStyle = img.getAttribute('style');
    const imgTitle = img.getAttribute('title');

    // Remove any invalid XML tags as per http://validator.w3.org
    svgClone.removeAttribute('xmlns:a');

    if (imgID) svgClone.setAttribute('id', imgID);
    if (imgStyle) svgClone.setAttribute('style', imgStyle);
    if (imgClass) svgClone.setAttribute('class', imgClass + ' replaced-svg');
    if (imgTitle) {
      svgClone.setAttribute('title', imgTitle);
      // Prepend title element for accessibility
      const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      titleEl.textContent = imgTitle;
      svgClone.insertBefore(titleEl, svgClone.firstChild);
    }

    // Replace image with new SVG
    img.replaceWith(svgClone);
  });

  deferreds.push(replacementPromise);
}

/**
 * Replaces all img elements with class 'svg' with inline SVG.
 * @param urlToDeferredMap - Map of URLs to Promises for caching
 * @param deferreds - Array of Promise objects for tracking completion
 */
function fixAllSVG(urlToDeferredMap?: Record<string, Promise<string>>, deferreds?: Promise<void>[]): void {
  urlToDeferredMap = urlToDeferredMap || svgMap;
  deferreds = deferreds || svgDeferreds;

  document.querySelectorAll<HTMLImageElement>('img.svg').forEach(img => {
    fixSVG(img, urlToDeferredMap, deferreds);
  });

  // Wait for all SVG replacements to complete, then wrap SVGs with titles
  Promise.all(deferreds).then(() => {
    document.querySelectorAll('svg[title]').forEach(svg => {
      const parent = svg.parentElement;
      if (parent && !parent.getAttribute('title')) {
        const wrapper = document.createElement('span');
        wrapper.setAttribute('title', svg.getAttribute('title') || '');
        svg.parentNode?.insertBefore(wrapper, svg);
        wrapper.appendChild(svg);
      }
    });
  });
}

/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified delay has elapsed since the last invocation.
 * @param func - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns Debounced function
 */
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let inDebounce: ReturnType<typeof setTimeout> | undefined;
  return function(this: any, ...args: Parameters<T>) {
    const context = this;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(function() { func.apply(context, args); }, delay);
  };
}

/**
 * Creates a throttled function that only invokes the provided function
 * at most once per specified time period.
 * @param func - The function to throttle
 * @param limit - The time limit in milliseconds
 * @returns Throttled function
 */
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): (...args: Parameters<T>) => void {
  let lastFunc: ReturnType<typeof setTimeout> | undefined;
  let lastRan: number | undefined;
  return function(this: any, ...args: Parameters<T>) {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan!) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

/**
 * CSRF-protected fetch utility.
 * Automatically manages CSRF tokens in session storage and includes them in requests.
 */
const csrf: CsrfFetch = {
  SESSION_STORAGE_KEY: 'csrf-tokens',
  CSRF_TOKEN_ENDPOINT: '/csrf-token',
  fetch: async (url: string, options?: RequestInit): Promise<Response> => {
    const b6pCsrfToken = 'B6P-CSRF-TOKEN';
    // Get origin of url
    const origin = /^https?:\/\//.test(url) ? new URL(url).origin : location.origin;

    const tokenStr = sessionStorage.getItem(csrf.SESSION_STORAGE_KEY);
    let tokens: Record<string, string>;
    if (tokenStr) {
      tokens = JSON.parse(tokenStr);
    } else {
      sessionStorage.setItem(csrf.SESSION_STORAGE_KEY, '{}');
      tokens = {};
    }

    if (!tokens[origin]) {
      tokens[origin] = await fetch(`${origin}${csrf.CSRF_TOKEN_ENDPOINT}`).then(r => r.text());
    }
    options = options || {};
    options.headers = options.headers || {};
    (options.headers as Record<string, string>)[b6pCsrfToken] = tokens[origin];
    let response = await fetch(url, options);
    if (response.status === 403) {
      tokens[origin] = await fetch(`${origin}${csrf.CSRF_TOKEN_ENDPOINT}`).then(r => r.text());
      (options.headers as Record<string, string>)[b6pCsrfToken] = tokens[origin];
      response = await fetch(url, options);
    }
    const newToken = response.headers.get(b6pCsrfToken);
    if (newToken) {
      tokens[origin] = newToken;
    }
    // Get storage again in case it was updated during the fetch
    const currentTokens = JSON.parse(sessionStorage.getItem(csrf.SESSION_STORAGE_KEY) || '{}');
    // Merge the two objects
    tokens = { ...currentTokens, ...tokens };
    sessionStorage.setItem(csrf.SESSION_STORAGE_KEY, JSON.stringify(tokens));
    return response;
  },
  getFreshToken: async (origin: string = location.origin): Promise<string> => {
    const token = await fetch(`${origin}${csrf.CSRF_TOKEN_ENDPOINT}`).then(r => r.text());
    try {
      const tokens = JSON.parse(sessionStorage.getItem(csrf.SESSION_STORAGE_KEY) || '{}') as Record<string, string>;
      tokens[origin] = token;
      sessionStorage.setItem(csrf.SESSION_STORAGE_KEY, JSON.stringify(tokens));
    } catch (_error) {
      // sessionStorage may be unavailable; the token still works for this request.
    }
    return token;
  }
};

/**
 * B6P namespace for application-level utilities.
 */
const B6P: B6PNamespace = {
  /**
   * Runs a function after async merge reports have loaded, or immediately if not in merge report context.
   * @param fnc - Function to run after merge reports load
   * @param fnc2 - Optional function to run if not in merge report context (defaults to fnc)
   */
  runAfterAsyncMergeReports: function(fnc: () => void, fnc2?: () => void): void {
    if (typeof fnc !== "function") {
      throw new Error("fnc passed to `runAfterAsyncMergeReports` is not a function!");
    }
    if (fnc2 == null) {
      fnc2 = fnc;
    }
    if (typeof fnc2 !== "function") {
      throw new Error("fnc2 passed to `runAfterAsyncMergeReports` is not a function!");
    }
    if (typeof __mergeReports !== "undefined") {
      window.addEventListener('mergeReportsLoad', fnc, { once: true });
    } else {
      fnc2();
    }
  },

  /**
   * Escapes HTML special characters to prevent XSS attacks.
   * @param text - The text to escape
   * @returns HTML-escaped text
   */
  escapeHtml: function(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Shows a brief toast notification that fades away.
   * @param message - The message to display
   * @param durationMs - How long to show before fading (default 2000ms)
   */
  showToast: function(message: string, durationMs: number = 2000): void {
    const t = document.createElement('div');
    t.textContent = message;
    t.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:12px 24px;border-radius:6px;z-index:999999;font-size:14px;opacity:1;transition:opacity 0.5s';
    document.body.appendChild(t);
    setTimeout(function() {
      t.style.opacity = '0';
      setTimeout(function() { t.remove(); }, 500);
    }, durationMs);
  },

};

/**
 * Custom element that fetches HTML from a URL and renders it inline.
 *
 * Attributes:
 * - `src` - URL to fetch content from. Changing this re-fetches automatically.
 * - `run-scripts` - When `"true"`, activates `<script>` tags in the fetched content.
 * - `csrf` - Controls CSRF token handling: `"true"` forces csrf.fetch,
 *   `"false"` forces plain fetch, omit for auto-detection by origin.
 *
 * Same-origin requests include CSRF tokens by default. Cross-origin requests
 * use plain fetch (remote server must provide CORS headers).
 *
 * Blocked by HTMLFilter in user-supplied content to prevent injection.
 *
 * @example
 * ```html
 * <b-include src="/shared/content/fragment.jsp"></b-include>
 * <b-include src="/path/with/scripts.html" run-scripts="true"></b-include>
 * ```
 */
class BInclude extends HTMLElement {
	private abortController: AbortController | null = null;

	static get observedAttributes(): string[] {
		return ['src'];
	}

	get src(): string | null {
		return this.getAttribute('src');
	}

	set src(val: string | null) {
		if (val) {
			this.setAttribute('src', val);
		} else {
			this.removeAttribute('src');
		}
	}

	get runScripts(): boolean {
		return this.getAttribute('run-scripts') === 'true';
	}

	set runScripts(val: boolean) {
		this.setAttribute('run-scripts', String(val));
	}

	get csrf(): boolean | null {
		const attr = this.getAttribute('csrf');
		if (attr === 'true') return true;
		if (attr === 'false') return false;
		return null; // auto-detect
	}

	set csrf(val: boolean | null) {
		if (val === null) {
			this.removeAttribute('csrf');
		} else {
			this.setAttribute('csrf', String(val));
		}
	}

	connectedCallback(): void {
		this.load();
	}

	disconnectedCallback(): void {
		this.abortController?.abort();
	}

	attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
		if (name === 'src' && oldValue !== null && oldValue !== newValue) {
			this.load();
		}
	}

	private isSameOrigin(url: string): boolean {
		if (!/^https?:\/\//.test(url)) return true; // relative URL
		return new URL(url).origin === location.origin;
	}

	private showLoading(): void {
		this.innerHTML = '<style>@keyframes b-include-spin{to{transform:rotate(360deg)}}</style>'
			+ '<div style="display:flex;align-items:center;gap:8px;padding:8px;color:#888">'
			+ '<span style="width:16px;height:16px;border:2px solid #e0e0e0;border-top-color:#667eea;border-radius:50%;animation:b-include-spin .8s linear infinite;display:inline-block"></span>'
			+ 'Loading\u2026</div>';
	}

	private showError(): void {
		this.innerHTML = '<div style="padding:8px;color:#a00">'
			+ 'Failed to load content. '
			+ '<a href="javascript:void(0)" style="color:inherit;text-decoration:underline">Reload</a>'
			+ '</div>';
		const link = this.querySelector('a');
		if (link) {
			link.addEventListener('click', (e: Event) => {
				e.preventDefault();
				this.load();
			});
		}
	}

	private async load(): Promise<void> {
		const src = this.src;
		if (!src) return;

		this.abortController?.abort();
		this.abortController = new AbortController();
		const options: RequestInit = { signal: this.abortController.signal };

		this.showLoading();

		try {
			const useCsrf = this.csrf ?? this.isSameOrigin(src);
			const response = useCsrf
				? await csrf.fetch(src, options)
				: await fetch(src, options);
			if (!response.ok) {
				console.error(`b-include: Failed to load ${src}: ${response.status} ${response.statusText}`);
				this.showError();
				return;
			}
			const html = await response.text();
			this.innerHTML = html;

			if (this.runScripts) {
				this.activateScripts();
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === 'AbortError') return;
			console.error(`b-include: Error loading ${src}:`, e);
			this.showError();
		}
	}

	private activateScripts(): void {
		const scripts = this.querySelectorAll('script');
		scripts.forEach(oldScript => {
			const newScript = document.createElement('script');
			for (const attr of oldScript.attributes) {
				newScript.setAttribute(attr.name, attr.value);
			}
			newScript.textContent = oldScript.textContent;
			oldScript.replaceWith(newScript);
		});
	}
}

customElements.define('b-include', BInclude);


// Run fixAllSVG on document ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fixAllSVG());
  } else {
    fixAllSVG();
  }
}

// Export for module usage
export {
  // Types
  Position,
  Size,
  BottomRightPosition,
  Sniffer,
  B6PNamespace,
  // Sniffer
  createSniffer,
  is,
  // DOM utilities
  findPosition,
  getObjectHeight,
  getObjectWidth,
  getObjectSize,
  getScrollPosition,
  getInnerSize,
  getScrollBottomRightPosition,
  getInternetExplorerVersion,
  // SVG utilities
  fixSVG,
  fixAllSVG,
  svgMap,
  svgDeferreds,
  parseSvgString,
  // Throttle/debounce
  debounce,
  throttle,
  // CSRF
  csrf,
  // B6P namespace
  B6P,
  BInclude
};

// Expose globally for backwards compatibility
const _global = globalThis as Record<string, unknown>;
_global.is = is;
_global.findPosition = findPosition;
_global.getObjectHeight = getObjectHeight;
_global.getObjectWidth = getObjectWidth;
_global.getObjectSize = getObjectSize;
_global.getScrollPosition = getScrollPosition;
_global.getInnerSize = getInnerSize;
_global.getScrollBottomRightPosition = getScrollBottomRightPosition;
_global.getInternetExplorerVersion = getInternetExplorerVersion;
_global.fixSVG = fixSVG;
_global.fixAllSVG = fixAllSVG;
_global.svgMap = svgMap;
_global.svgDeferreds = svgDeferreds;
_global.debounce = debounce;
_global.throttle = throttle;
_global.csrf = csrf;
_global.B6P = _global.B6P || B6P;
_global.BInclude = BInclude;

// Script resource notification
if (typeof _SR_ !== 'undefined' && _SR_ !== null) {
  _SR_.notify("dom.js");
}
